import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import {
    rosTypeAudio,
    rosTypeCompressedImage,
    rosTypeJointState,
    rosTypeLaserScan,
    rosTypeMarkerArray,
    rosTypePointCloud2,
    rosTypeVideoFrame,
} from "@formant/core/model/rosTypes";
import {
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { TeleopMode } from "@formant/core/model/TeleopMode";
import { Page } from "@formant/ui/component/common/Page";
import { VStack } from "@formant/ui/component/common/VStack";

import { audioPathsMapForDevices } from "../../utils/audioPathsMapForDevice";
import { videoPathsMapForDevices } from "../../utils/videoPathsMapForDevice";
import { CircleButton } from "../common/CircleButton";
import { Scrollable } from "../common/Scrollable";
import { TabWorkflowButtons } from "../common/TabWorkflowButtons";
import { ValidationMessages } from "../common/ValidationMessages";
import { Container } from "../Container";
import { rosTopicsForDevices } from "../Observe/utils/rosTopicsForDevices";
import * as styles from "./RealtimeTelemetryConfiguration.scss";
import { TeleopAudioConfiguration } from "./TeleopAudioConfiguration";
import { TeleopButtonConfiguration } from "./TeleopButtonConfiguration";
import { TeleopCompressedImageConfiguration } from "./TeleopCompressedImageConfiguration";
import { TeleopHardwareVideoConfiguration } from "./TeleopHardwareVideoConfiguration";
import { TeleopJointStateConfiguration } from "./TeleopJointStateConfiguration";
import { TeleopStreamConfigurationGroup } from "./TeleopStreamConfigurationGroup";

export interface ITeleopConfigurationProps {
    devices?: IDevice[];
    document: IDeviceConfigurationDocument;
    dirty: boolean;
    unsaved?: boolean;
    validation: IValidation;
    saveCopy?: string;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onSave: () => void;
    loading: boolean;
    popup?: boolean;
}

@observer
export class RealtimeTelemetryConfiguration extends Component<ITeleopConfigurationProps> {
    @observable
    private editingCompressedImage?: string;

    @observable
    private editingButton?: string;

    @observable
    private editingJointState?: string;

    @observable
    private editingAudio?: string;

    @observable
    private editingHardwareVideo?: string;

    public render() {
        const {
            devices,
            dirty,
            unsaved,
            document,
            validation,
            saveCopy,
            onSave,
            loading,
            popup,
        } = this.props;

        const joystickValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.joysticks"),
        );

        const streamValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.rosStreams"),
        );

        const customValidationKeys = Object.keys(validation).filter(_ =>
            _.includes("document.teleop.customStreams"),
        );

        const rosTopics = rosTopicsForDevices(devices);
        const videoPathsMap = videoPathsMapForDevices(devices);

        const audioPathsMap = audioPathsMapForDevices(devices);

        const commonProps = {
            topics: rosTopics,
            document,
            onAddRosStream: this.onAddRosStream,
            onAddCustomStream: this.onAddCustomStream,
            onAddHardwareStream: this.onAddHardwareStream,
            onDeleteStream: this.onDeleteStream,
        };

        return (
            <Page
                className={classnames(
                    styles["teleop-configuration"],
                    popup && styles.popup,
                )}
            >
                <Scrollable>
                    <Container className={styles.container}>
                        <VStack innerSpacing="lg">
                            <TeleopStreamConfigurationGroup
                                label={"Video sources"}
                                mode="observe"
                                customInputType="h264-video-frame"
                                rosInputTypes={[
                                    "sensor_msgs/CompressedImage",
                                    "sensor_msgs/Image",
                                    "formant/H264VideoFrame",
                                ]}
                                rosOutputTypes={[
                                    rosTypeCompressedImage,
                                    rosTypeVideoFrame,
                                ]}
                                addStreamCopy={"Add video source"}
                                disableCustom={true}
                                enableHardware={true}
                                nestedHardwarePathsMap={videoPathsMap}
                                specificOnEdit={
                                    this.onOpenCompressedImageConfig
                                }
                                editableTypes={[
                                    "sensor_msgs/CompressedImage",
                                    "sensor_msgs/Image",
                                    "formant/H264VideoFrame",
                                ]}
                                help={
                                    "The video source can be configured to use a ROS image topic or a hardware video source (e.g. usb camera on /dev/video0)."
                                }
                                max={4}
                                onCreatePreChildren={
                                    this.onCreateImageItemPreChildren
                                }
                                {...commonProps}
                            />

                            <>
                                <TeleopStreamConfigurationGroup
                                    label="Audio"
                                    mode="observe"
                                    customInputType="audio-chunk"
                                    rosInputTypes={[
                                        "audio_common_msgs/AudioData",
                                    ]}
                                    specificOnEdit={this.onOpenAudioConfig}
                                    editableTypes={[
                                        "audio_common_msgs/AudioData",
                                    ]}
                                    rosOutputTypes={[rosTypeAudio]}
                                    enableHardware={true}
                                    hardwarePathsMap={audioPathsMap}
                                    disableCustom={true}
                                    max={1}
                                    {...commonProps}
                                />
                            </>

                            <>
                                <TeleopStreamConfigurationGroup
                                    label="Joint states"
                                    mode="observe"
                                    customInputType="joint-state"
                                    rosInputTypes={["sensor_msgs/JointState"]}
                                    rosOutputTypes={[rosTypeJointState]}
                                    disableCustom={false}
                                    editableTypes={[rosTypeJointState]}
                                    max={2}
                                    {...commonProps}
                                />
                                <TeleopStreamConfigurationGroup
                                    label="Geometry"
                                    mode="observe"
                                    customInputType="marker-array"
                                    rosInputTypes={[
                                        "visualization_msgs/MarkerArray",
                                    ]}
                                    rosOutputTypes={[rosTypeMarkerArray]}
                                    disableCustom={true}
                                    max={1}
                                    {...commonProps}
                                />
                                <TeleopStreamConfigurationGroup
                                    label="PointCloud"
                                    mode="observe"
                                    customInputType="point-cloud"
                                    rosInputTypes={[
                                        "sensor_msgs/PointCloud2",
                                        "sensor_msgs/LaserScan",
                                    ]}
                                    rosOutputTypes={[
                                        rosTypeLaserScan,
                                        rosTypePointCloud2,
                                    ]}
                                    disableCustom={true}
                                    max={1}
                                    {...commonProps}
                                />
                                <TeleopStreamConfigurationGroup
                                    label="Json"
                                    mode="observe"
                                    customInputType="json-string"
                                    disableCustom={false}
                                    max={16}
                                    rosInputTypes={[]}
                                    rosOutputTypes={[]}
                                    {...commonProps}
                                />
                            </>

                            <ValidationMessages
                                validation={validation}
                                keys={[
                                    ...joystickValidationKeys,
                                    ...streamValidationKeys,
                                    ...customValidationKeys,
                                ]}
                            />
                            <TeleopCompressedImageConfiguration
                                visible={!!this.editingCompressedImage}
                                streamName={this.editingCompressedImage}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingCompressedImage}
                            />
                            <TeleopAudioConfiguration
                                visible={!!this.editingAudio}
                                streamName={this.editingAudio}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingAudio}
                            />
                            <TeleopButtonConfiguration
                                visible={!!this.editingButton}
                                streamName={this.editingButton}
                                topics={rosTopics}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingButtonConfig}
                            />
                            <TeleopJointStateConfiguration
                                visible={!!this.editingJointState}
                                streamName={this.editingJointState}
                                topics={rosTopics}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingJointStateConfig}
                            />
                            <TeleopHardwareVideoConfiguration
                                visible={!!this.editingHardwareVideo}
                                streamName={this.editingHardwareVideo}
                                document={document}
                                onChange={this.props.onChange}
                                onClose={this.onCloseEditingHardwareVideoConfig}
                            />
                        </VStack>
                    </Container>
                </Scrollable>
                <TabWorkflowButtons
                    dirty={dirty}
                    unsaved={unsaved}
                    onSave={onSave}
                    loading={loading}
                    saveCopy={saveCopy}
                />
            </Page>
        );
    }

    private onOpenCompressedImageConfig =
        (name: string, sourceType: string) => () => {
            if (sourceType === "hardware") {
                this.editingHardwareVideo = name;
            } else {
                this.editingCompressedImage = name;
            }
        };

    private onOpenAudioConfig = (name: string, _: string) => () => {
        this.editingAudio = name;
    };

    private onCloseEditingAudio = () => {
        this.editingAudio = undefined;
    };
    private onCloseEditingCompressedImage = () => {
        this.editingCompressedImage = undefined;
    };

    private onCloseEditingButtonConfig = () => {
        this.editingButton = undefined;
    };

    private onCloseEditingJointStateConfig = () => {
        this.editingJointState = undefined;
    };

    private onCloseEditingHardwareVideoConfig = () => {
        this.editingHardwareVideo = undefined;
    };

    private onDeleteStream = (
        streamName: string,
        mode: TeleopMode,
        label?: string,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const customStreams = document.teleop?.customStreams || [];
        const rosStreams = document.teleop?.rosStreams || [];
        const hardwareStreams = document.teleop?.hardwareStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];

        let deleteStream = true;

        if (label) {
            // delete stream label
            const stream = customStreams.find(
                _ => _.name === streamName && _.mode === mode,
            );
            if (stream && stream.labels) {
                stream.labels = stream.labels.filter(_ => _ !== label);
                deleteStream = stream.labels.length === 0;
            }
        }

        if (deleteStream) {
            // can't use map, since views is a tuple
            views.forEach(
                (_, index) =>
                    (views[index] = _.streamName === streamName ? {} : _),
            );
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                rosStreams: rosStreams.filter(
                    ({ topicName, mode: topicMode }) =>
                        topicName !== streamName || topicMode !== mode,
                ),
                customStreams: customStreams.filter(
                    _ =>
                        _.name !== streamName ||
                        _.mode !== mode ||
                        (_.name === streamName &&
                            _.mode === mode &&
                            _.labels !== undefined &&
                            _.labels?.length !== 0),
                ),
                hardwareStreams: hardwareStreams.filter(
                    _ => _.name !== streamName || _.mode !== mode,
                ),
            },
        });
    };

    private onAddRosStream = (stream: IDeviceTeleopRosStreamConfiguration) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.rosStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            "formant/H264VideoFrame",
            "sensor_msgs/CompressedImage",
        ];

        if (viewSlot > -1 && supportedTypes.some(_ => _ === stream.topicType)) {
            views[viewSlot] = { streamName: stream.topicName };
        }
        if (stream.topicType === "audio_common_msgs/AudioData") {
            stream.audioCodec = "raw";
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                rosStreams: [...streams, stream],
            },
        });
    };

    private onAddCustomStream = (
        stream: IDeviceTeleopCustomStreamConfiguration,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.customStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            rtcCompressedImageStreamType,
            rtcH264VideoFrameStreamType,
        ];

        if (
            viewSlot > -1 &&
            supportedTypes.some(_ => _ === stream.rtcStreamType)
        ) {
            views[viewSlot] = { streamName: stream.name };
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                customStreams: [
                    ...streams.filter(
                        _ => _.name !== stream.name || _.mode !== stream.mode,
                    ),
                    stream,
                ],
            },
        });
    };

    private onAddHardwareStream = (
        stream: IDeviceTeleopHardwareStreamConfiguration,
    ) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const streams = document.teleop?.hardwareStreams || [];
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];
        const viewSlot = views.findIndex(_ => _.streamName === undefined);

        const supportedTypes = [
            rtcCompressedImageStreamType,
            rtcH264VideoFrameStreamType,
        ];

        if (
            viewSlot > -1 &&
            supportedTypes.some(_ => _ === stream.rtcStreamType)
        ) {
            views[viewSlot] = { streamName: stream.name };
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
                hardwareStreams: [
                    ...streams.filter(
                        _ => _.name !== stream.name || _.mode !== stream.mode,
                    ),
                    stream,
                ],
            },
        });
    };

    private onChangeView = (index: number, view?: ITeleopViewConfiguration) => {
        const { document, onChange } = this.props;
        const teleop = document.teleop || {};
        const views = document.teleop?.views || [{}, {}, {}, {}, {}];

        views[index] = view || {};

        onChange({
            ...document,
            teleop: {
                ...teleop,
                views,
            },
        });
    };

    private onCreateImageItemPreChildren = (
        _source: string,
        name: string,
        _type: string,
    ) => {
        const { document } = this.props;
        const firstView = document.teleop?.views && document.teleop?.views[0];
        let isSelected = false;
        if (firstView) {
            if (firstView.streamName === name) {
                isSelected = true;
            }
        }
        return (
            <CircleButton
                icon={isSelected ? "radio_filled" : "radio"}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.changePrimaryView(name);
                }}
                data-tooltip={
                    isSelected
                        ? "Your primary video source"
                        : "Set this as your primary video source"
                }
            />
        );
    };

    private changePrimaryView = (name: string) => {
        this.onChangeView(0, {
            streamName: name,
        });
    };
}
